import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const featuredTestimonials = [
  {
    id: "left",
    quote:
      '"The real-time monitoring and predictive analytics have helped us improve our development velocity by 40%."',
    reviewer: "Avnish Gevariya",
    title: "CMO, BrandGrid",
    rating: 5,
    tone: "side",
    avatar: "/Avinash_Gevariya_image.webp",
  },
  {
    id: "center",
    quote:
      "\"I have had a great experience in terms of productivity when using TeamGrid, as it has provided an effective solution for managing my team's productivity. While there are multiple software tools that focus on task management, this platform specifically emphasizes tracking working hours — what each person is doing and how much time is allocated to each project. This not only offers a clear view of my team's productivity but also provides better insight into client time allocation.\"",
    reviewer: "Jay Mistry",
    title: "Founder, Craft Idea",
    rating: 5,
    tone: "center",
    avatar: null,
  },
  {
    id: "right",
    quote:
      '"This has transformed how we understand our team\'s productivity. The insights are incredibly accurate."',
    reviewer: "Rupesh Sanghavi",
    title: "CEO, Boson tech",
    rating: 5,
    tone: "side",
    avatar: "/Rupesh_sanghvi.webp",
  },
];

const Testimonials = ({
  title = "Trusted by Leading Teams",
  subtitle = "See what our customers say about TeamGrid",
  className = "",
}) => {
  return (
    <section
      className={`testimonials-section relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-10 sm:pt-12 md:pt-14 pb-16 md:pb-20 bg-black overflow-hidden ${className}`}
    >
      <div className="max-w-[1150px] mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-center mb-12"
        >
          <div
            className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
            style={{
              background: "rgba(0, 209, 193, 0.12)",
              border: "1px solid rgba(0, 209, 193, 0.35)",
              fontSize: "12px",
              fontWeight: 600,
              color: "#00D1C1",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <span>CUSTOMER SUCCESS</span>
          </div>
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-[#E5E7EB] max-w-[900px] mx-auto text-center mb-3">
            {title}
          </h2>
          <p className="text-[#E5E7EB] text-[18px] leading-[28px] max-w-[640px] mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.45fr_1fr] gap-6 lg:gap-10 items-center">
          {featuredTestimonials.map((item, index) => {
            const isCenter = item.tone === "center";

            return (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.12 * index }}
                className={`rounded-2xl border flex flex-col transition-all duration-300 ${
                  isCenter
                    ? "bg-[#18C8C2] border-[#3DE5DF] shadow-[0_14px_28px_rgba(24,200,194,0.24)] p-6 md:p-7 lg:p-8 md:min-h-[320px] lg:min-h-[380px] lg:scale-[1.05]"
                    : "bg-[#0B162A] border-[#1D3156] p-5 md:p-6 aspect-square lg:scale-90 lg:opacity-40 lg:translate-y-6"
                }`}
                style={!isCenter ? { filter: "blur(0)" } : undefined}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        isCenter
                          ? "text-[#111111] fill-[#111111]"
                          : "text-[#F5C94C] fill-[#F5C94C]"
                      }`}
                    />
                  ))}
                </div>

                <p
                  className={`${
                    isCenter
                      ? "text-[#0A0A0A] text-[14px] md:text-[15px] lg:text-[16px] font-medium tracking-normal"
                      : "text-[#CBD5E1] text-[18px] font-normal tracking-normal"
                  }`}
                  style={{ lineHeight: isCenter ? "1.6" : "28px" }}
                >
                  {item.quote}
                </p>

                <div
                  className={`mt-auto pt-4 border-t ${
                    isCenter ? "border-[#13B6B0]" : "border-[#223352]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      {item.avatar ? (
                        <img
                          src={item.avatar}
                          alt={item.reviewer}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-full h-full flex items-center justify-center font-semibold text-base ${
                            isCenter
                              ? "bg-[#0A0A0A] text-[#18C8C2]"
                              : "bg-[#18C8C2] text-[#0A0A0A]"
                          }`}
                          aria-label={item.reviewer}
                        >
                          {item.reviewer.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <p
                        className={`font-semibold text-sm leading-tight ${
                          isCenter ? "text-[#0C0C0C]" : "text-[#E5E7EB]"
                        }`}
                      >
                        {item.reviewer}
                      </p>
                      <p
                        className={`text-xs leading-tight ${
                          isCenter ? "text-black/70" : "text-[#73839F]"
                        }`}
                      >
                        {item.title}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
